/**
 * Shared button-selection limit for Course Builder vs PYP.
 *
 * PYP keeps the historical 10-if-many-options / else-3 rule (and Stage 2 default).
 * Course Builder uses Excel selection_limit / selectionLimit from part meta
 * and never the PYP 10/3 heuristic.
 */

export function parseSelectionLimitFromMeta(titleMeta) {
  const meta = Array.isArray(titleMeta) ? titleMeta[0] : titleMeta
  const raw = meta?.selection_limit ?? meta?.selectionLimit
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 1) return null
  return Math.floor(n)
}

/**
 * @returns {number|null} Positive cap, or null when Course Builder has no meta limit
 *   (no click cap; Next enables after at least one selection).
 */
export function resolveButtonSelectionLimit({
  isCourseBuilder = false,
  titleMeta,
  optionCount = 0,
  stage = null,
  stageDefaultLimit
} = {}) {
  const optionN = Number(optionCount) || 0

  if (stage) {
    return stageDefaultLimit
  }

  if (isCourseBuilder) {
    const fromServer = parseSelectionLimitFromMeta(titleMeta)
    if (fromServer == null) return null
    if (optionN > 0) return Math.min(fromServer, optionN)
    return fromServer
  }

  return optionN >= 10 ? 10 : 3
}

export function countPersistedButtonSelections(
  buttonValues,
  { stage = null, valuesToUpdate = null } = {}
) {
  if (!Array.isArray(buttonValues)) return 0

  return buttonValues.filter((buttonValue) => {
    if (
      !buttonValue ||
      !buttonValue.buttonSelections ||
      !Array.isArray(buttonValue.buttonSelections)
    ) {
      return false
    }

    if (valuesToUpdate) {
      return (
        buttonValue.buttonSelections.length > 0 &&
        (stage ? buttonValue.buttonSelections[0]?.pivot?.stage === 2 : true) &&
        !valuesToUpdate.find((value) => value.id === buttonValue.id && !value.isSelected)
      )
    }

    return (
      buttonValue.buttonSelections.length > 0 &&
      (stage ? buttonValue.buttonSelections[0]?.pivot?.stage === 2 : true)
    )
  }).length
}

export function isButtonSelectionRequirementMet({
  isCourseBuilder = false,
  titleMeta,
  optionCount = 0,
  selectedCount = 0,
  stage = null,
  stageDefaultLimit
} = {}) {
  const limit = resolveButtonSelectionLimit({
    isCourseBuilder,
    titleMeta,
    optionCount,
    stage,
    stageDefaultLimit
  })

  if (isCourseBuilder && (limit == null || limit < 1)) {
    return selectedCount >= 1
  }

  return selectedCount >= limit
}
